import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import Popover from '@mui/material/Popover';
import { UserContext } from '../../contexts/UserContext';
import TinyMCEEditor, { TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import { fetchData } from '../../helpers/apiUtils';
import { VORSCHLAG_GRUND_TEXT, vorschlagEinsetzen } from './vorschlagEinsetzen';
import { formatGermanTime } from '../../helpers/germanTime';
import {
  Abschnitt,
  Feld,
  GefahrBestaetigung,
  Kennzeichen,
  MehrOptionen,
  NurAdmin,
  BEGRIFF,
  datumDe,
  useFeldpruefung,
  ladeBeispiele,
} from '../../components/marketingForm';
import type { Beispiel } from '../../components/marketingForm';

/**
 * Erinnerungsmails pro Webinar — Bestätigung + zeitgesteuerte Erinnerungen.
 *
 * Plan 2026-10-22 (B1): der Reiter zeigt das GEWÄHLTE Webinar, die Liste aller Kampagnen ist Nebenansicht.
 * Je Mail EIN zusammengefasster Status (sperrt / Hinweis / OK) statt roter Regel neben grünem KI-Satz —
 * Personas lasen beides gleichzeitig und wussten nicht, was gilt.
 *
 * Was die Oberfläche weiterhin bewusst zeigt (seit dem Mailchimp-Weg):
 * 1. Den BERECHNETEN Sendezeitpunkt je Mail (Server rechnet, nichts gespeichert).
 * 2. Die Datums-Warnung beim Übernehmen aus einem früheren Webinar.
 *
 * Requests und Nutzlasten sind dieselben wie vorher (PUT /emails/:id mit dem geänderten Feldsatz,
 * PUT /campaigns/:id mit status/auto_arm, copy-from, zeitbezug-ack, preview, test-send, DELETE).
 */

interface CampaignListRow {
  id: number;
  webinar_id: string;
  name: string;
  status: string;
  occurrence_date: string | null;
  webinar_title: string | null;
  display_title?: string | null;
  start_date: string;
  concept: string | null;
  slot_count: number;
  sent_count: number;
  mc_list_id: string | null;
  /** Pausenflag der automatischen Aktivierung: 0 nach manuellem Deaktivieren, 1 sonst. */
  auto_arm?: number;
}

/** Noch ausstehendes Heat-Up-Mailing — GET /api/webinar-management/webinars/:id/scheduled-mailings */
interface ScheduledMailing {
  id: number;
  campaign_id: number;
  client_code: string;
  esp_type: string;
  scheduled_datetime: string;
  status: string;
}

type KiBefundZeile = { zitat: string; warum: string; vorschlag: string };

interface ZeitbezugBefund {
  regel: string;
  feld: 'Betreff' | 'Preheader' | 'Text';
  zitat: string;
  warum: string;
}

interface SlotRow {
  id: number;
  slot_number: number;
  trigger_type: 'on_signup' | 'offset';
  offset_minutes: number | null;
  /** 'HH:MM' = Uhrzeit-Mail; offset_minutes ist dann der Tag (0 Webinartag, -1440 Vortag). */
  send_clock: string | null;
  max_late_minutes: number;
  channel: string;
  subject: string;
  preview_text: string | null;
  from_name: string;
  reply_to: string | null;
  html_body: string | null;
  status: string;
  sent_at: string | null;
  recipients_count: number | null;
  error_message: string | null;
  send_at_preview: string | null;
  /** "So 27.09. 06:00" */
  send_at_label?: string | null;
  send_at_error: string | null;
  warnings: string[];
  /** Prüfungen des GESPEICHERTEN Stands (node-app zeitbezug.ts + Namensregel F7). */
  zeitbezug?: {
    befunde: ZeitbezugBefund[];
    hash: string;
    freigegeben: boolean;
    sperrt: boolean;
    zeit_sperrt?: boolean;
    namen_sperrt?: boolean;
    /** „Befund ist OK“ gilt für den aktuellen Stand — deckt Regel- UND KI-Befunde. */
    ack_aktuell?: boolean;
    /** KI-Zeitcheck (alle 15 min) für den aktuellen Stand; null = nicht geprüft (Bestätigung). */
    ki?: {
      status: 'offen' | 'pending' | 'done' | 'error';
      befunde: { zitat: string; warum: string; vorschlag: string }[];
      model: string | null;
      checked_at: string | null;
      attempts: number;
    } | null;
  };
}

interface SourceRow {
  id: number;
  name: string;
  webinar_id: string;
  webinar_title: string | null;
  concept: string | null;
  start_date: string;
  filled_slots: number;
}

type Banner = { severity: 'success' | 'warning' | 'error' | 'info'; title: string; lines: string[] };

const RAUM_LINK = '{WEBINAR_ROOM_LINK}';

/**
 * Wohin die Testmail geht — NUR zur Anzeige am Knopf. Die Regel steht im Backend
 * (`node-app/src/services/webinarReminder/testEmpfaenger.ts`: exakte Domains, sonst Till); die Antwort `sent_to`
 * bleibt maßgeblich und wird nach dem Klick gemeldet.
 */
const TEST_DOMAINS = ['yes-investmedia.de', 'finanztrends.de', 'finanztrends.info'];
const TEST_RUECKFALL = 'till.meier@yes-investmedia.de';
function testZiel(email: string | null | undefined): { adresse: string; eigene: boolean } {
  const a = String(email ?? '').trim().toLowerCase();
  const m = /^[^\s@,;<>"]+@([a-z0-9.-]+)$/i.exec(a);
  return m && TEST_DOMAINS.includes(m[1]) ? { adresse: a, eigene: true } : { adresse: TEST_RUECKFALL, eigene: false };
}

/** Sichtbarer Text eines HTML-Fragments (Vorschau in der Auswahl) — DOMParser führt keine Skripte aus. */
function htmlAlsText(html: string, max = 200): string {
  const t = (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** Tagesversätze einer Uhrzeit-Mail — spiegelt UHRZEIT_TAGESVERSAETZE in render.ts. */
const UHRZEIT_TAGE: { value: number; label: string }[] = [
  { value: 0, label: 'Webinartag' },
  { value: -1440, label: 'Vortag' },
  { value: -2880, label: '2 Tage vorher' },
];

const KAMPAGNEN_STATUS: Record<string, string> = {
  draft: 'Entwurf — Versand nicht aktiv',
  active: 'Versand aktiv',
  completed: 'abgeschlossen',
  cancelled: 'abgebrochen',
};
const MAIL_STATUS: Record<string, string> = {
  draft: 'Entwurf',
  scheduled: 'geplant',
  sending: 'wird gerade verschickt',
  sent: 'verschickt',
  failed: 'fehlgeschlagen',
  skipped_late: 'zu spät — übersprungen',
};
const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  failed: 'error',
  skipped_late: 'error',
  active: 'success',
  completed: 'default',
  cancelled: 'default',
};

/** Platzhalter-Knöpfe: was eingefügt wird und was der Empfänger liest. */
const ZEIT_PLATZHALTER: { token: string; knopf: string; liest: string }[] = [
  { token: '{WEBINAR_TAG_RELATIV_GROSS}', knopf: 'Heute / Morgen (Satzanfang)', liest: '„Heute“ bzw. „Morgen“ — am Satzanfang' },
  { token: '{WEBINAR_TAG_RELATIV}', knopf: 'heute / morgen', liest: '„heute“ bzw. „morgen“ — im Satz' },
  { token: '{WEBINAR_ZEITPUNKT_RELATIV}', knopf: 'heute um 18:00 Uhr', liest: 'z. B. „heute um 18:00 Uhr“ oder „morgen um 18:00 Uhr“' },
  { token: '{ZEIT_BIS_START}', knopf: 'in einer Stunde', liest: 'Restzeit bis zum Beginn, z. B. „in einer Stunde“' },
  { token: '{ZEIT_BIS_START_GROSS}', knopf: 'In einer Stunde (Satzanfang)', liest: 'Restzeit am Satzanfang, z. B. „In einer Stunde“' },
];
const SONST_PLATZHALTER: { token: string; knopf: string; liest: string }[] = [
  { token: '{WEBINAR_TITLE}', knopf: 'Webinar-Titel', liest: 'der öffentliche Titel des Webinars' },
  { token: '{WEBINAR_DAY_WRITTEN}', knopf: 'Wochentag', liest: 'z. B. „Sonntag“' },
  { token: '{WEBINAR_TIME}', knopf: 'Uhrzeit', liest: 'z. B. „18:00“' },
];

/** „Mail 2: Webinartag 06:00“ — aus den echten Mail-Daten, nicht hart codiert. */
function mailBeschreibung(slot: SlotRow): string {
  if (slot.trigger_type === 'on_signup') return 'Bestätigung direkt nach der Anmeldung';
  const o = Number(slot.offset_minutes ?? 0);
  if (slot.send_clock) {
    const tag = UHRZEIT_TAGE.find((t) => t.value === o)?.label ?? `${Math.abs(o / 1440)} Tage vorher`;
    return `${tag} ${slot.send_clock} Uhr`;
  }
  if (o === 0) return 'zum Beginn';
  const a = Math.abs(o);
  const dauer = a >= 60 && a % 60 === 0 ? `${a / 60} ${a === 60 ? 'Stunde' : 'Stunden'}` : `${a} Minuten`;
  return o < 0 ? `${dauer} vor Beginn` : `${dauer} nach Beginn`;
}

function gehtRaus(slot: SlotRow): string {
  if (slot.trigger_type === 'on_signup') return 'sofort nach jeder Anmeldung';
  if (slot.send_at_label || slot.send_at_preview) return String(slot.send_at_label || slot.send_at_preview);
  return 'Zeitpunkt nicht bestimmbar';
}

const istGesperrt = (slot: SlotRow) => slot.status === 'sent' || slot.status === 'sending';

/** Ein zusammengefasster Status je Mail — aus den Prüfungen des Servers plus dem, was gerade im Formular steht. */
function mailStatus(slot: SlotRow): {
  stufe: 'sperrt' | 'hinweis' | 'ok';
  sperrGruende: string[];
  hinweise: string[];
  /** KI-Befunde getrennt von den Text-Hinweisen — sie tragen einen Vorschlag zum Übernehmen. */
  kiBefunde: KiBefundZeile[];
  quittierbar: boolean;
} {
  const sperrGruende: string[] = [];
  const hinweise: string[] = [];
  const zb = slot.zeitbezug;
  if (!String(slot.subject ?? '').trim() || !String(slot.html_body ?? '').trim()) {
    sperrGruende.push('Betreff oder Text fehlt — beides ist Pflicht.');
  }
  if (!String(slot.html_body ?? '').includes(RAUM_LINK)) {
    sperrGruende.push('Der Raum-Link fehlt — ohne ihn kommt der Empfänger nicht ins Webinar. Knopf „Raum-Link einfügen“ nutzen.');
  }
  const offen = zb && !zb.freigegeben ? zb.befunde : [];
  for (const b of offen) {
    const name = b.regel === 'fremder_webinarname';
    const zeile = `${name ? 'Anderes Webinar' : 'Zeitangabe'} (${b.feld}): „${b.zitat}“ — ${b.warum}`;
    const sperrend = name ? zb?.namen_sperrt ?? zb?.sperrt : zb?.zeit_sperrt ?? zb?.sperrt;
    (sperrend ? sperrGruende : hinweise).push(zeile);
  }
  const ki = zb?.ki;
  const kiBefunde = ki?.status === 'done' && ki.befunde.length > 0 && !zb?.ack_aktuell ? ki.befunde : [];
  for (const w of slot.warnings ?? []) hinweise.push(w);
  if (slot.error_message) hinweise.push(`Fehler beim letzten Versand: ${slot.error_message}`);
  const quittierbar = offen.length > 0 || (ki?.status === 'done' && ki.befunde.length > 0 && !zb?.ack_aktuell);
  return {
    stufe: sperrGruende.length ? 'sperrt' : hinweise.length || kiBefunde.length ? 'hinweis' : 'ok',
    sperrGruende,
    hinweise,
    kiBefunde,
    quittierbar: Boolean(quittierbar),
  };
}

const ReminderTab: React.FC<{ webinarId?: string | null }> = ({ webinarId }) => {
  const [campaigns, setCampaigns] = useState<CampaignListRow[]>([]);
  const [alleKampagnen, setAlleKampagnen] = useState<CampaignListRow[] | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [campaign, setCampaign] = useState<CampaignListRow | null>(null);
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [geladen, setGeladen] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState<number | ''>('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [dirty, setDirty] = useState<Record<number, Partial<SlotRow>>>({});
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFehler, setDeleteFehler] = useState<string | null>(null);
  // Deaktivieren-Dialog: erst zeigen, was WEITERLÄUFT (Heat-Up-Mailings gatet die Kampagne nicht),
  // dann bestätigen. `null` = Abruf lief nicht/fehlte — bestätigen bleibt erlaubt, mit Hinweis.
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateMailings, setDeactivateMailings] = useState<ScheduledMailing[] | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const loadCampaigns = useCallback(async () => {
    const q = webinarId ? `?webinar_id=${encodeURIComponent(webinarId)}` : '';
    const r = await fetchData<CampaignListRow[]>(`/api/webinar-reminders/campaigns${q}`);
    if (r.success && r.data) setCampaigns(r.data);
    setGeladen(true);
  }, [webinarId]);

  const loadAlle = useCallback(async () => {
    const r = await fetchData<CampaignListRow[]>('/api/webinar-reminders/campaigns');
    setAlleKampagnen(r.success && r.data ? r.data : []);
  }, []);

  /**
   * `cancelled` ist kein Zierrat: beim schnellen Wechsel zwischen Kampagnen gewinnt sonst die
   * zuletzt eintreffende Antwort, nicht die zuletzt angeklickte Kampagne.
   */
  // `behalte`: ungespeicherte Änderungen anderer Mails, die das Neuladen überleben sollen (saveSlot).
  const loadDetail = useCallback(async (id: number, isCancelled?: () => boolean, behalte?: Record<number, Partial<SlotRow>>) => {
    setLoading(true);
    // Erst leeren, dann holen — leer und ehrlich schlägt gefüllt und falsch.
    setCampaign(null);
    setSlots([]);
    const r = await fetchData<{ campaign: CampaignListRow; emails: SlotRow[] }>(
      `/api/webinar-reminders/campaigns/${id}`,
    );
    if (isCancelled?.()) return;
    if (r.success && r.data) {
      setCampaign(r.data.campaign);
      // Nur auf noch änderbare Mails zurücklegen — eine inzwischen versendete nimmt nichts mehr an.
      const offen: Record<number, Partial<SlotRow>> = {};
      for (const e of r.data.emails) if (behalte?.[e.id] && !istGesperrt(e)) offen[e.id] = behalte[e.id];
      setSlots(r.data.emails.map((e) => (offen[e.id] ? ({ ...e, ...offen[e.id] } as SlotRow) : e)));
      setDirty(offen);
    }
    setLoading(false);
  }, []);

  // Anderes Webinar gewählt (Prop wechselt ohne Neuaufbau): Auswahl zurücksetzen.
  const letzteWebinarId = useRef(webinarId);
  useEffect(() => {
    if (letzteWebinarId.current === webinarId) return;
    letzteWebinarId.current = webinarId;
    setSelectedId(null);
    setCampaign(null);
    setSlots([]);
    setDirty({});
    setGeladen(false);
  }, [webinarId]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Das gewählte Webinar öffnet direkt seine (jüngste) Kampagne.
  useEffect(() => {
    if (webinarId && selectedId === null && campaigns.length > 0 && campaigns[0].webinar_id === webinarId) {
      setSelectedId(campaigns[0].id);
    }
  }, [webinarId, campaigns, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    loadDetail(selectedId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [selectedId, loadDetail]);

  const showWarnings = (title: string, warnings?: string[]) => {
    if (warnings?.length) setBanner({ severity: 'warning', title, lines: warnings });
    else setBanner({ severity: 'success', title, lines: [] });
  };

  const createCampaign = async () => {
    if (!webinarId) return;
    const r = (await fetchData<{ id: number }>('/api/webinar-reminders/campaigns', {
      method: 'POST',
      body: JSON.stringify({ webinar_id: webinarId }),
    })) as { success: boolean; data?: { id: number }; error?: string };
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    await loadCampaigns();
    if (alleKampagnen) await loadAlle();
    setSelectedId(r.data!.id);
  };

  const openCopy = async () => {
    // Der Dialog hängt dauerhaft im Baum, sein State überlebt das Schließen — Auswahl zurücksetzen.
    setCopySource('');
    const r = await fetchData<SourceRow[]>('/api/webinar-reminders/source-campaigns');
    if (r.success && r.data) setSources(r.data.filter((s) => s.id !== selectedId));
    setCopyOpen(true);
  };

  const runCopy = async () => {
    if (!selectedId || !copySource) return;
    const r = (await fetchData(`/api/webinar-reminders/campaigns/${selectedId}/copy-from/${copySource}`, {
      method: 'POST',
    })) as { success: boolean; error?: string; warnings?: string[] };
    setCopyOpen(false);
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    await loadDetail(selectedId);
    showWarnings('Texte übernommen — bitte Datum, Titel und Betreff in jeder Mail prüfen', r.warnings);
  };

  const saveSlot = async (slot: SlotRow) => {
    const patch = dirty[slot.id];
    if (!patch) return;
    const r = (await fetchData(`/api/webinar-reminders/emails/${slot.id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    })) as { success: boolean; error?: string; warnings?: string[] };
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    // Ungespeicherte Änderungen der ANDEREN Mails mitnehmen — sonst verwirft das Neuladen z. B. einen
    // übernommenen KI-Vorschlag in Mail 4, wenn Mail 3 gespeichert wird (Codex-Review 27.10.). Aus dem
    // Ref, nicht aus der Closure: während des PUT kann in einer anderen Mail weiter getippt worden sein.
    const andere = Object.fromEntries(
      Object.entries(dirtyRef.current).filter(([id]) => Number(id) !== slot.id),
    ) as Record<number, Partial<SlotRow>>;
    await loadDetail(selectedId!, undefined, andere);
    showWarnings(`Mail ${slot.slot_number} gespeichert`, r.warnings);
  };

  /**
   * Kampagne löschen. Der Server entscheidet, OB das darf (Entwurf, keine Mail außerhalb Entwurf,
   * keine Bestätigungen) — die Bedingung steht dort im WHERE eines einzigen DELETE.
   */
  const deleteCampaign = async () => {
    if (!selectedId || deleting) return;
    setDeleting(true);
    setDeleteFehler(null);
    const r = (await fetchData(`/api/webinar-reminders/campaigns/${selectedId}`, {
      method: 'DELETE',
    })) as { success: boolean; error?: string };
    setDeleting(false);
    if (!r.success) {
      setDeleteFehler(r.error || 'Löschen fehlgeschlagen');
      return;
    }
    setDeleteOpen(false);
    // Auswahl allein reicht NICHT: der Detail-Effekt steigt bei !selectedId sofort aus.
    setSelectedId(null);
    setCampaign(null);
    setSlots([]);
    setDirty({});
    await loadCampaigns();
    if (alleKampagnen) await loadAlle();
    setBanner({ severity: 'success', title: 'Kampagne gelöscht', lines: [] });
  };

  const setStatus = async (status: string) => {
    if (!selectedId) return;
    const r = (await fetchData(`/api/webinar-reminders/campaigns/${selectedId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })) as {
      success: boolean;
      error?: string;
      dispatch_plan?: { slot_number: number; send_at_preview: string | null; send_at_label?: string | null }[];
    };
    if (!r.success) {
      return setBanner({
        severity: 'error',
        title: 'Versand nicht aktiviert',
        lines: [r.error || 'Fehler', 'Was fehlt, steht oben in der roten Zeile der betroffenen Mail.'],
      });
    }
    await loadDetail(selectedId);
    await loadCampaigns();
    if (alleKampagnen) await loadAlle();
    // Den Sendeplan ANZEIGEN, nicht nur „aktiv“ melden.
    setBanner({
      severity: 'success',
      title: status === 'active' ? 'Versand aktiviert — so gehen die Mails raus:' : 'Versand deaktiviert',
      lines: (r.dispatch_plan || [])
        .filter((p) => p.send_at_preview)
        .map((p) => `Mail ${p.slot_number}: ${p.send_at_label || p.send_at_preview}`),
    });
  };

  const openDeactivate = async () => {
    if (!campaign) return;
    setDeactivateMailings(null);
    setDeactivateLoading(true);
    setDeactivateOpen(true);
    const r = await fetchData<ScheduledMailing[]>(
      `/api/webinar-management/webinars/${encodeURIComponent(campaign.webinar_id)}/scheduled-mailings`,
    );
    setDeactivateLoading(false);
    if (r.success && r.data) setDeactivateMailings(r.data);
  };

  const confirmDeactivate = async () => {
    if (deactivating) return;
    setDeactivating(true);
    try {
      await setStatus('draft');
    } finally {
      setDeactivating(false);
      setDeactivateOpen(false);
      setDeactivateMailings(null);
    }
  };

  /** Pausierte Automatik wieder einschalten — der Server setzt sonst nur bei status='active'. */
  const reenableAutoArm = async () => {
    if (!selectedId) return;
    const r = (await fetchData(`/api/webinar-reminders/campaigns/${selectedId}`, {
      method: 'PUT',
      body: JSON.stringify({ auto_arm: 1 }),
    })) as { success: boolean; error?: string };
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    await loadDetail(selectedId);
    await loadCampaigns();
    if (alleKampagnen) await loadAlle();
    setBanner({
      severity: 'success',
      title: 'Automatik wieder an',
      lines: ['Der Versand wird automatisch aktiviert, sobald alle Voraussetzungen erfüllt sind.'],
    });
  };

  const preview = async (slot: SlotRow) => {
    const r = (await fetchData<{ html: string }>(`/api/webinar-reminders/emails/${slot.id}/preview`, {
      method: 'POST',
    })) as { success: boolean; data?: { html: string }; error?: string };
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    setPreviewHtml(r.data!.html);
  };

  const testSend = async (slot: SlotRow) => {
    const r = (await fetchData<{ sent_to: string }>(`/api/webinar-reminders/emails/${slot.id}/test-send`, {
      method: 'POST',
    })) as { success: boolean; data?: { sent_to: string }; error?: string };
    setBanner(
      r.success
        ? {
            severity: 'success',
            title: `Testmail zu Mail ${slot.slot_number} ging an ${r.data!.sent_to}`,
            lines: ['Betreff beginnt mit „[TEST]“. Es wird der GESPEICHERTE Stand verschickt.'],
          }
        : { severity: 'error', title: r.error || 'Fehler', lines: [] },
    );
  };

  const patchSlot = (id: number, field: keyof SlotRow, value: unknown) => {
    setDirty((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));
    setSlots((s) => s.map((x) => (x.id === id ? ({ ...x, [field]: value } as SlotRow) : x)));
  };

  /**
   * Zwischen „Minuten vor Beginn“ und „Tag + Uhrzeit“ umschalten. Beide Felder zusammen setzen —
   * bei Uhrzeit ist der Versatz der TAG; ein stehengebliebenes -60 wäre ein ungültiger Tag.
   */
  const setZeitModus = (slot: SlotRow, modus: 'minuten' | 'uhrzeit') => {
    if (modus === 'uhrzeit') {
      patchSlot(slot.id, 'send_clock', '06:00');
      patchSlot(slot.id, 'offset_minutes', 0);
    } else {
      patchSlot(slot.id, 'send_clock', null);
      patchSlot(slot.id, 'offset_minutes', -60);
    }
  };

  /**
   * „Befund ist OK“ — gibt den GESPEICHERTEN Stand frei, den der Server per Fingerabdruck wiedererkennt.
   * Bewusst NICHT an `dirty` gegatet: TinyMCE markiert beim Laden jede Mail als geändert. Sicher ist es
   * trotzdem: wer danach speichert, ändert den Stand, und die Freigabe ist weg. Nur DIESE Mail lokal
   * nachziehen — loadDetail leert `dirty` und würde Eingaben in den anderen Mails verwerfen.
   */
  const ackZeitbezug = async (slot: SlotRow) => {
    if (!slot.zeitbezug) return;
    const r = (await fetchData(`/api/webinar-reminders/emails/${slot.id}/zeitbezug-ack`, {
      method: 'POST',
      body: JSON.stringify({ hash: slot.zeitbezug.hash }),
    })) as { success: boolean; error?: string };
    if (!r.success) return setBanner({ severity: 'error', title: r.error || 'Fehler', lines: [] });
    setSlots((s) =>
      s.map((x) =>
        x.id === slot.id && x.zeitbezug
          ? {
              ...x,
              zeitbezug: {
                ...x.zeitbezug,
                freigegeben: x.zeitbezug.befunde.length > 0,
                sperrt: false,
                zeit_sperrt: false,
                namen_sperrt: false,
                ack_aktuell: true,
              },
            }
          : x,
      ),
    );
    setBanner({ severity: 'success', title: `Mail ${slot.slot_number}: Befund als OK markiert`, lines: [] });
  };

  const nutzer = useContext(UserContext);
  const testAn = testZiel(nutzer?.email);
  // Ohne gewähltes Webinar ist `campaigns` schon die ungefilterte Liste.
  const alleListe = webinarId ? alleKampagnen : campaigns;
  const listenZeile = useMemo(
    () => [...campaigns, ...(alleKampagnen ?? [])].find((c) => c.id === selectedId) ?? null,
    [campaigns, alleKampagnen, selectedId],
  );
  const kuerzel = listenZeile?.concept || campaign?.webinar_id || '';
  const titel = campaign?.display_title || campaign?.webinar_title || listenZeile?.webinar_title || '';
  const fremdeKampagne = Boolean(webinarId && campaign && campaign.webinar_id !== webinarId);
  const stati = slots.map((s) => ({ slot: s, st: mailStatus(s) }));
  const sperrende = stati.filter((x) => x.st.stufe === 'sperrt');
  const hatInhalt = slots.some((s) => String(s.subject ?? '').trim() || String(s.html_body ?? '').trim());
  const zielMitInhalt = slots
    .filter((s) => String(s.subject ?? '').trim() || String(s.html_body ?? '').trim())
    .map((s) => s.slot_number);

  return (
    <Box>
      {banner && (
        <Alert severity={banner.severity} onClose={() => setBanner(null)} sx={{ mb: 2 }}>
          <AlertTitle>{banner.title}</AlertTitle>
          {banner.lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </Alert>
      )}

      {!webinarId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Kein Webinar gewählt. Öffne die Erinnerungsmails über die Webinar-Liste (Reiter „Webinare“) — oder wähle
          unten unter „Alle Kampagnen“ eine Kampagne aus.
        </Alert>
      )}

      {webinarId && geladen && campaigns.length === 0 && (
        <Abschnitt
          titel="Erinnerungsmails für dieses Webinar"
          art="pflicht"
          erklaerung="Noch keine Erinnerungsmails angelegt. Ohne sie bekommt ein Anmelder weder Bestätigung noch Zugangslink — das Webinar wird dann auch nicht beworben."
        >
          <Button variant="contained" startIcon={<AddIcon />} onClick={createCampaign}>
            Erinnerungsmails anlegen
          </Button>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Legt vier leere Mails mit den üblichen Versandzeiten an. Danach am schnellsten: „Aus früherem Webinar
            übernehmen“.
          </Typography>
        </Abschnitt>
      )}

      {loading && <CircularProgress sx={{ my: 2 }} />}

      {campaign && !loading && (
        <>
          {/* Kopf: welches Webinar, welcher Stand, was als Nächstes */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#ffffff', borderColor: '#e5e7eb' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontSize: 17, fontWeight: 600 }}>
                Erinnerungsmails: {titel || campaign.webinar_id}
              </Typography>
              {kuerzel && <Chip size="small" label={kuerzel} />}
              <Chip
                size="small"
                color={campaign.status === 'active' ? 'success' : 'default'}
                label={KAMPAGNEN_STATUS[campaign.status] ?? campaign.status}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Termin: {datumDe(String(campaign.start_date ?? ''))} · {slots.length} Mails, alle {slots.length} sind
              Pflicht. Jede braucht Betreff, Text und den Raum-Link.
            </Typography>
            {fremdeKampagne && (
              <Alert
                severity="warning"
                sx={{ mt: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setSelectedId(campaigns[0]?.id ?? null)}>
                    Zurück zum gewählten Webinar
                  </Button>
                }
              >
                Du bearbeitest gerade die Erinnerungsmails eines ANDEREN Webinars (aus „Alle Kampagnen“).
              </Alert>
            )}
            {campaign.status === 'active' ? (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                Versand aktiv — die Mails gehen automatisch zu den angezeigten Zeiten raus. Nichts zu tun.
              </Alert>
            ) : sperrende.length > 0 ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Noch nicht bereit: {sperrende.map((x) => `Mail ${x.slot.slot_number}`).join(', ')}{' '}
                {sperrende.length === 1 ? 'braucht' : 'brauchen'} noch etwas (rote Zeile in der Mail). Danach wird der
                Versand automatisch aktiviert, sobald Anmelde- und Bestätigungsseite live sind.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Alle Mails sind vollständig. Der Versand wird automatisch aktiviert, sobald Anmelde- und
                Bestätigungsseite live sind (Prüfung alle 15 Minuten).
              </Alert>
            )}
          </Paper>

          {/* Schritt 1: übernehmen */}
          <Abschnitt
            titel="Schritt 1: Texte aus einem früheren Webinar übernehmen"
            art="optional"
            erklaerung="Der schnellste Weg: alle Mails eines früheren Webinars kopieren und danach Titel, Datum und Inhalt anpassen."
          >
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={openCopy}
              disabled={!selectedId || campaign.status !== 'draft'}
            >
              Aus früherem Webinar übernehmen
            </Button>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Fragt vorher, wenn Mails überschrieben würden.
            </Typography>
            {campaign.status !== 'draft' && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                Nur möglich, solange der Versand nicht aktiv ist.
              </Typography>
            )}
          </Abschnitt>

          {/* Schritt 2: die Mails */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            Schritt 2: Die {slots.length} Mails prüfen und anpassen
          </Typography>
          {stati.map(({ slot, st }) => (
            <MailKarte
              key={slot.id}
              slot={slot}
              status={st}
              webinarId={campaign.webinar_id}
              dirty={Boolean(dirty[slot.id])}
              onPatch={(f, v) => patchSlot(slot.id, f, v)}
              onZeitModus={(m) => setZeitModus(slot, m)}
              onSave={() => saveSlot(slot)}
              onPreview={() => preview(slot)}
              onTest={() => testSend(slot)}
              onAck={() => ackZeitbezug(slot)}
              testAn={testAn}
            />
          ))}

          {/* Schritt 3: Versand */}
          <Abschnitt
            titel={`Schritt 3: ${BEGRIFF.versandAktivieren}`}
            art="automatisch"
            erklaerung="Passiert automatisch, sobald alle Mails vollständig sind und Anmelde- und Bestätigungsseite live sind (Prüfung alle 15 Minuten). Hier ist normalerweise nichts zu tun."
          >
            {campaign.status === 'active' ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip color="success" label="Versand aktiv" />
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<PauseIcon />}
                  onClick={openDeactivate}
                  disabled={deactivateLoading || deactivating}
                >
                  Versand deaktivieren
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setStatus('active')}
                  disabled={!selectedId}
                >
                  {BEGRIFF.versandAktivieren} (von Hand)
                </Button>
                {listenZeile?.auto_arm === 0 ? (
                  <>
                    <Chip color="warning" size="small" label="Automatik pausiert" />
                    <Button size="small" onClick={reenableAutoArm}>
                      Automatik wieder an
                    </Button>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Automatik an — du musst hier normalerweise nichts klicken.
                  </Typography>
                )}
              </Stack>
            )}
            {campaign.status !== 'active' && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, maxWidth: '90ch' }}>
                Den Knopf brauchst du nur, wenn die Automatik pausiert ist (nach „Versand deaktivieren“) oder die Mails
                schon starten sollen, bevor die Seiten live sind. Fehlt in einer Mail noch etwas, wird nichts aktiviert —
                es erscheint nur eine Meldung mit dem ersten Grund (die roten Zeilen in den Mails).
              </Typography>
            )}
            {listenZeile?.auto_arm === 0 && campaign.status !== 'active' && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                Nach manuellem Deaktivieren schaltet die Automatik diese Mails NICHT von selbst wieder an.
                „Automatik wieder an“ hebt das auf.
              </Typography>
            )}
          </Abschnitt>

          {/* Gefahrenbereich — bewusst weit weg von „Versand aktivieren“ */}
          <Box sx={{ mt: 4, p: 2, border: '1px dashed #f5c2bd', borderRadius: '6px', bgcolor: '#fffafa' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#a12a1f' }}>
              Gefahrenbereich
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              Löscht alle Erinnerungsmails dieser Kampagne. Nur möglich, solange noch keine Mail verschickt wurde.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                setDeleteFehler(null);
                setDeleteOpen(true);
              }}
              disabled={!selectedId || deleting}
            >
              Kampagne löschen …
            </Button>
          </Box>
        </>
      )}

      {/* Nebenansicht: alle Kampagnen */}
      <Box sx={{ mt: 4 }}>
        <Abschnitt
          titel="Alle Kampagnen"
          art="optional"
          eingeklappt={Boolean(webinarId)}
          erklaerung="Nebenansicht: Erinnerungsmails aller Webinare, z. B. um eine ältere Kampagne nachzusehen."
        >
          {alleListe === null ? (
            <Button size="small" variant="outlined" onClick={loadAlle}>
              Alle Kampagnen laden
            </Button>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 2, backgroundColor: '#ffffff', maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Kampagne', 'Webinar', 'Termin', 'Mails verschickt', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alleListe.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      selected={c.id === selectedId}
                      onClick={() => setSelectedId(c.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{c.name}</TableCell>
                      <TableCell>
                        {c.concept && <Chip label={c.concept} size="small" sx={{ mr: 1 }} />}
                        {c.webinar_title}
                      </TableCell>
                      <TableCell>{datumDe(String(c.start_date))}</TableCell>
                      <TableCell>
                        {c.sent_count} von {c.slot_count}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={KAMPAGNEN_STATUS[c.status] ?? c.status}
                          color={STATUS_COLOR[c.status] || 'default'}
                        />
                        {c.auto_arm === 0 && (
                          <Chip size="small" color="warning" label="Automatik pausiert" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {alleListe.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          Keine Kampagnen.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {webinarId && campaigns.length > 0 && (
            <NurAdmin>
              <Button size="small" startIcon={<AddIcon />} onClick={createCampaign}>
                Weitere Kampagne für dieses Webinar anlegen (z. B. für einen weiteren Termin)
              </Button>
            </NurAdmin>
          )}
        </Abschnitt>
      </Box>

      {/* Deaktivieren */}
      <Dialog open={deactivateOpen} onClose={() => !deactivating && setDeactivateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Versand der Erinnerungsmails deaktivieren?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{campaign?.name}</strong> wird wieder Entwurf: Bestätigungs- und Erinnerungsmails gehen nicht mehr
            raus, auch nicht an neue Anmelder.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Die Automatik wird dabei pausiert — sie aktiviert den Versand nicht 15 Minuten später wieder. Wieder
            einschalten über „{BEGRIFF.versandAktivieren}“ oder „Automatik wieder an“.
          </Typography>
          {deactivateLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Geplante Heat-Up-Mailings werden geprüft…
              </Typography>
            </Stack>
          )}
          {!deactivateLoading && deactivateMailings === null && (
            <Alert severity="warning">
              Geplante Heat-Up-Mailings konnten nicht geprüft werden. Falls welche geplant sind, laufen sie
              unabhängig davon weiter — bitte unter Webinar-Mailings nachsehen.
            </Alert>
          )}
          {!deactivateLoading && deactivateMailings && deactivateMailings.length > 0 && (
            <Alert severity="info">
              <AlertTitle>Diese bereits geplanten Heat-Up-Mailings laufen WEITER:</AlertTitle>
              {deactivateMailings.map((m) => (
                <div key={m.id}>
                  {m.client_code.toUpperCase()} · {m.esp_type} ·{' '}
                  {formatGermanTime(m.scheduled_datetime, 'DD.MM.YYYY HH:mm')}
                </div>
              ))}
            </Alert>
          )}
          {!deactivateLoading && deactivateMailings && deactivateMailings.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Keine Heat-Up-Mailings mehr geplant.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)} disabled={deactivating}>
            Abbrechen
          </Button>
          <Button color="warning" variant="contained" onClick={confirmDeactivate} disabled={deactivating || deactivateLoading}>
            {deactivating ? 'Deaktiviere…' : 'Versand deaktivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <GefahrBestaetigung
        open={deleteOpen}
        titel="Erinnerungsmails löschen?"
        wirkung={
          <>
            <strong>{campaign?.name}</strong> wird mit allen {slots.length} Mails und Texten gelöscht. Anmelder dieses
            Webinars bekommen danach keine Bestätigung und keine Erinnerung, bis neue Mails angelegt und aktiviert
            sind — und das Webinar wird so lange nicht beworben. Das lässt sich nicht rückgängig machen. Wurde schon
            eine Mail verschickt, lehnt das System das Löschen ab und nennt den Grund.
          </>
        }
        knopfText="Endgültig löschen"
        bestaetigungsWort={kuerzel || undefined}
        laeuft={deleting}
        fehler={deleteFehler}
        onBestaetigen={deleteCampaign}
        onAbbrechen={() => setDeleteOpen(false)}
      />

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aus früherem Webinar übernehmen</DialogTitle>
        <DialogContent>
          {hatInhalt ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Überschreibt den vorhandenen Entwurf</AlertTitle>
              Mail {zielMitInhalt.join(', ')} {zielMitInhalt.length === 1 ? 'hat' : 'haben'} schon Texte. Übernehmen
              ersetzt Betreff, Vorschauzeile, Absender, Text und Versandzeiten ALLER Mails durch die der Vorlage.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Die Mails sind noch leer — es wird nichts überschrieben.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Übernommen werden nur Texte. Titel, Datum und Zahlen stammen danach noch vom früheren Webinar — die
            Prüfung zeigt an, wo.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Vorlage</InputLabel>
            <Select label="Vorlage" value={copySource} onChange={(e) => setCopySource(e.target.value as number)}>
              {sources.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {datumDe(String(s.start_date))} · {s.concept || s.webinar_id} · {s.webinar_title || s.name}
                  {` (${s.filled_slots} Mails)`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color={hatInhalt ? 'warning' : 'primary'} onClick={runCopy} disabled={!copySource}>
            {hatInhalt ? 'Entwurf überschreiben' : 'Übernehmen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vorschau */}
      <Dialog open={previewHtml !== null} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>Vorschau (gespeicherter Stand)</DialogTitle>
        <DialogContent dividers>
          {/* srcDoc statt innerHTML: der Mail-Body bringt eigene Styles mit. Sandbox ohne allow-scripts. */}
          <iframe
            title="Erinnerungsmail-Vorschau"
            srcDoc={previewHtml || ''}
            sandbox=""
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewHtml(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
        Testmails gehen an deine eigene Adresse, wenn sie auf @yes-investmedia.de, @finanztrends.de oder
        @finanztrends.info endet — sonst an den Admin (till.meier@yes-investmedia.de). Die Meldung nach dem Klick
        nennt die Adresse.
      </Typography>
    </Box>
  );
};

/**
 * KI-Befunde einer Mail mit Knopf „Vorschlag übernehmen“ — im roten wie im gelben Block derselbe Renderer.
 * Der Knopf füllt nur das Feld; gespeichert wird von Hand. Ist die Stelle nicht eindeutig, bleibt er aus.
 */
const KiBefundListe: React.FC<{
  befunde: KiBefundZeile[];
  slot: SlotRow;
  gesperrt: boolean;
  onUebernehmen: (feld: 'subject' | 'preview_text' | 'html_body', wert: string) => void;
}> = ({ befunde, slot, gesperrt, onUebernehmen }) => (
  <Box sx={{ mt: 0.5 }}>
    {befunde.map((b, i) => {
      const felder = { subject: slot.subject, preview_text: slot.preview_text, html_body: slot.html_body };
      const r = b.vorschlag ? vorschlagEinsetzen(felder, b.zitat, b.vorschlag) : null;
      return (
        <Box key={`${i}-${b.zitat}`} sx={{ mb: 0.5 }}>
          <div>
            • Zusätzliche KI-Prüfung (sperrt nie): „{b.zitat}“ — {b.warum}
            {b.vorschlag ? ` · Vorschlag: ${b.vorschlag}` : ''}
          </div>
          {r && !gesperrt && (
            <Tooltip title={r.ok ? 'Setzt den Vorschlag an die Stelle ein. Danach prüfen und SPEICHERN.' : VORSCHLAG_GRUND_TEXT[r.grund]}>
              <span>
                <Button size="small" variant="outlined" color="inherit" disabled={!r.ok} sx={{ mt: 0.25 }}
                  onClick={() => r.ok && onUebernehmen(r.feld, r.wert)}>
                  Vorschlag übernehmen
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    })}
  </Box>
);

/** Eine Mail: Kopf mit Zeitpunkt + Pflicht, EIN zusammengefasster Status, Felder, Platzhalter-Knöpfe, Aktionen. */
const MailKarte: React.FC<{
  slot: SlotRow;
  status: ReturnType<typeof mailStatus>;
  webinarId: string;
  dirty: boolean;
  onPatch: (field: keyof SlotRow, value: unknown) => void;
  onZeitModus: (m: 'minuten' | 'uhrzeit') => void;
  onSave: () => void;
  onPreview: () => void;
  onTest: () => void;
  onAck: () => void;
  testAn: { adresse: string; eigene: boolean };
}> = ({ slot, status, webinarId, dirty, onPatch, onZeitModus, onSave, onPreview, onTest, onAck, testAn }) => {
  const gesperrt = istGesperrt(slot);
  const n = slot.slot_number;
  const mitBeispielen = n >= 1 && n <= 4;
  const betreffPruefung = useFeldpruefung(mitBeispielen ? `reminder.${n}.subject` : '', webinarId);
  // Wohin ein Platzhalter-Knopf schreibt: das zuletzt fokussierte Feld dieser Mail (Standard: Text).
  // Refs leben in dieser Komponente, die je Mail-Id neu entsteht — kein Fremdfeld nach Kampagnenwechsel.
  const [ziel, setZiel] = useState<'subject' | 'preview_text' | 'html_body'>('html_body');
  const betreffRef = useRef<HTMLInputElement | null>(null);
  const preheaderRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<TinyMCEEditorRef | null>(null);
  const hatRaumLink = String(slot.html_body ?? '').includes(RAUM_LINK);
  const [vorschlagHinweis, setVorschlagHinweis] = useState<string | null>(null);
  const uebernehmeVorschlag = (feld: 'subject' | 'preview_text' | 'html_body', wert: string) => {
    onPatch(feld, wert);
    if (feld === 'html_body') editorRef.current?.setContent(wert);
    setVorschlagHinweis('Vorschlag übernommen — bitte lesen und SPEICHERN. Erst dann prüft die KI den neuen Text.');
  };
  const kiListe = status.kiBefunde.length > 0 && (
    <>
      <KiBefundListe befunde={status.kiBefunde} slot={slot} gesperrt={gesperrt} onUebernehmen={uebernehmeVorschlag} />
      {vorschlagHinweis && <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{vorschlagHinweis}</Typography>}
    </>
  );

  // Fertiger Betreff zum geplanten Versand — über die lesende Vorschau-Route (rendert den GESPEICHERTEN Stand,
  // schreibt nichts). Neu geholt, wenn der Server einen neuen Stand meldet (Fingerabdruck der Prüfung).
  // `quelle` = Betreff, für den gerendert wurde — nur anzeigen, solange im Feld noch genau der steht
  // (an `dirty` lässt sich das nicht hängen: der Editor markiert beim Laden jede Mail als geändert).
  const [fertigerBetreff, setFertigerBetreff] = useState<{ quelle: string; text: string } | null>(null);
  const standKennung = slot.zeitbezug?.hash ?? '';
  const betreffMitPlatzhalter = /\{[A-Z_:.dmY]+\}/.test(String(slot.subject ?? ''));
  useEffect(() => {
    setFertigerBetreff(null);
    if (!betreffMitPlatzhalter || !String(slot.html_body ?? '').trim()) return;
    const quelle = String(slot.subject ?? '');
    let abgebrochen = false;
    fetchData<{ html: string; subject: string }>(`/api/webinar-reminders/emails/${slot.id}/preview`, {
      method: 'POST',
    }).then((r) => {
      if (!abgebrochen && r.success && r.data?.subject) setFertigerBetreff({ quelle, text: r.data.subject });
    });
    return () => {
      abgebrochen = true;
    };
    // Nur bei neuem gespeicherten Stand — nicht bei jedem Tastendruck.
  }, [slot.id, standKennung]);

  const einfuegen = (token: string, wohin: typeof ziel = ziel) => {
    if (gesperrt) return;
    if (wohin === 'html_body') {
      if (editorRef.current) editorRef.current.insertContent(token);
      else onPatch('html_body', `${slot.html_body || ''}${token}`);
      return;
    }
    const el = wohin === 'subject' ? betreffRef.current : preheaderRef.current;
    const alt = String((wohin === 'subject' ? slot.subject : slot.preview_text) ?? '');
    const von = el?.selectionStart ?? alt.length;
    const bis = el?.selectionEnd ?? alt.length;
    onPatch(wohin, alt.slice(0, von) + token + alt.slice(bis));
  };

  const zielName = ziel === 'subject' ? 'Betreff' : ziel === 'preview_text' ? 'Vorschauzeile' : 'Text';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        bgcolor: '#ffffff',
        borderColor: status.stufe === 'sperrt' ? '#f5c2bd' : '#e5e7eb',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
          Mail {n}: {mailBeschreibung(slot)}
        </Typography>
        <Kennzeichen art="pflicht" />
        <Tooltip title="Berechnet aus Webinartermin und Versandzeit — nicht gespeichert, kann also nicht veralten.">
          <Chip size="small" color="info" variant="outlined" label={`geht raus: ${gehtRaus(slot)}`} />
        </Tooltip>
        <Chip size="small" label={MAIL_STATUS[slot.status] ?? slot.status} color={STATUS_COLOR[slot.status] || 'default'} />
        <Chip
          size="small"
          variant="outlined"
          color={hatRaumLink ? 'success' : 'error'}
          label={hatRaumLink ? 'Raum-Link drin' : 'Raum-Link fehlt'}
        />
        <NurAdmin>
          <Chip size="small" variant="outlined" label={slot.channel === 'ses' ? 'SES' : 'Mailchimp'} />
        </NurAdmin>
        {slot.sent_at && (
          <Typography variant="caption" color="text.secondary">
            verschickt {formatGermanTime(slot.sent_at, 'DD.MM.YYYY HH:mm')}
            {slot.recipients_count !== null && ` · ${slot.recipients_count} Empfänger`}
          </Typography>
        )}
      </Stack>

      {slot.send_at_error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Versandzeit nicht bestimmbar — unter „Versandzeit ändern“ korrigieren.
          <NurAdmin> ({slot.send_at_error})</NurAdmin>
        </Alert>
      )}

      {/* EIN Status je Mail */}
      {gesperrt ? (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          Diese Mail ist {MAIL_STATUS[slot.status]} — sie lässt sich nicht mehr ändern.
        </Alert>
      ) : status.stufe === 'sperrt' ? (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            status.quittierbar ? (
              <Button color="inherit" size="small" onClick={onAck}>
                Befund ist OK
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>Sperrt den Versand</AlertTitle>
          {status.sperrGruende.map((g) => (
            <div key={g}>• {g}</div>
          ))}
          {status.hinweise.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {status.hinweise.map((h) => (
                <div key={h}>Hinweis: {h}</div>
              ))}
            </Box>
          )}
          {kiListe}
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            Was tun: Text anpassen und speichern. „Befund ist OK“ nur klicken, wenn der beanstandete Text genau so
            gewollt ist (z. B. der genannte Name ist richtig, oder „morgen“ meint wirklich einen anderen Tag). Fehlender
            Text oder Raum-Link lässt sich nicht freigeben.
          </Typography>
        </Alert>
      ) : status.stufe === 'hinweis' ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            status.quittierbar ? (
              <Button color="inherit" size="small" onClick={onAck}>
                Befund ist OK
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>Hinweis — sperrt nicht</AlertTitle>
          {status.hinweise.map((h) => (
            <div key={h}>• {h}</div>
          ))}
          {kiListe}
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            Bitte lesen und bei Bedarf anpassen. „Befund ist OK“ blendet eine Prüfmeldung für genau diesen gespeicherten
            Text aus.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          OK — Betreff, Text und Raum-Link sind da, die Prüfungen haben nichts gefunden.
          {slot.zeitbezug?.freigegeben ? ' (Ein Befund wurde als OK markiert.)' : ''}
        </Alert>
      )}
      {!gesperrt && slot.zeitbezug?.ki && slot.zeitbezug.ki.status !== 'done' && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -1, mb: 1 }}>
          {slot.zeitbezug.ki.status === 'error'
            ? 'Die zusätzliche KI-Prüfung (sperrt nie) ist fehlgeschlagen — Zeitangaben bitte selbst lesen.'
            : 'Die zusätzliche KI-Prüfung (sperrt nie) hat diesen Stand noch nicht gesehen — sie läuft alle 15 Minuten, ab 7 Tage vor dem Webinar.'}
        </Typography>
      )}
      {dirty && !gesperrt && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -1, mb: 1 }}>
          Die Prüfung oben bezieht sich auf den gespeicherten Stand — nach dem Speichern wird neu geprüft.
        </Typography>
      )}

      <Feld
        titel="Betreff"
        art="pflicht"
        wofuer="Steht im Posteingang. Den Titel des Webinars am besten über den Knopf „Webinar-Titel“ einfügen, dann stimmt er immer."
        beispiele={
          mitBeispielen && !gesperrt
            ? { feld: `reminder.${n}.subject`, webinarId, onUebernehmen: (v) => onPatch('subject', v) }
            : undefined
        }
        befunde={betreffPruefung.befunde}
        pruefungLaeuft={betreffPruefung.laeuft}
      >
        <TextField
          fullWidth
          size="small"
          value={slot.subject || ''}
          disabled={gesperrt}
          inputRef={betreffRef}
          onFocus={() => setZiel('subject')}
          onChange={(e) => onPatch('subject', e.target.value)}
          onBlur={(e) => mitBeispielen && betreffPruefung.pruefen(e.target.value)}
        />
        {betreffMitPlatzhalter && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {fertigerBetreff && fertigerBetreff.quelle === String(slot.subject ?? '') ? (
              <>
                So sieht es aus (Beispiel zum Versandzeitpunkt): <strong>{fertigerBetreff.text}</strong>
              </>
            ) : (
              'Text in {…} wird beim Versand ersetzt — „Vorschau“ zeigt die fertige Mail (gespeicherter Stand).'
            )}
          </Typography>
        )}
      </Feld>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Feld
          titel="Vorschauzeile"
          art="optional"
          wofuer="Grauer Text neben dem Betreff im Posteingang. Leer: das Postfach zeigt den Anfang des Textes."
          beispiele={
            mitBeispielen && !gesperrt
              ? { feld: `reminder.${n}.preview_text`, webinarId, onUebernehmen: (v) => onPatch('preview_text', v) }
              : undefined
          }
          sx={{ flex: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            value={slot.preview_text || ''}
            disabled={gesperrt}
            inputRef={preheaderRef}
            onFocus={() => setZiel('preview_text')}
            onChange={(e) => onPatch('preview_text', e.target.value)}
          />
        </Feld>
        <Feld
          titel="Absender"
          art="pflicht"
          wofuer="Name, der im Posteingang als Absender steht. Ist vorbelegt — nicht leer lassen."
          beispiele={
            !gesperrt ? { feld: 'reminder.from_name', webinarId, onUebernehmen: (v) => onPatch('from_name', v) } : undefined
          }
          sx={{ flex: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            value={slot.from_name || ''}
            disabled={gesperrt}
            onChange={(e) => onPatch('from_name', e.target.value)}
          />
          {/\{[A-Z_:.dmY]+\}/.test(String(slot.from_name ?? '')) && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Text in {'{…}'} wird beim Versand ersetzt, z. B. {'{WEBINAR_TITLE}'} durch den Titel des Webinars.
            </Typography>
          )}
        </Feld>
      </Stack>

      <Feld
        titel="Text der Mail"
        art="pflicht"
        wofuer="Nur der Inhalt — Kopf, Fußzeile, Impressum und Abmeldelink kommen automatisch dazu. Muss den Raum-Link enthalten."
        aktion={
          mitBeispielen && !gesperrt ? (
            <TextVorlage
              n={n}
              webinarId={webinarId}
              hatText={Boolean(String(slot.html_body ?? '').replace(/<[^>]*>|&nbsp;|\s/g, ''))}
              onUebernehmen={(html) => {
                onPatch('html_body', html);
                editorRef.current?.setContent(html);
              }}
            />
          ) : undefined
        }
      >
        {!gesperrt && (
          <Box sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Tooltip title="Fügt einen Knopf „Jetzt zum Webinar-Raum“ in den Text ein. Jeder Empfänger bekommt darüber seinen persönlichen Zugang — ohne ihn kommt niemand ins Webinar.">
                <Button
                  size="small"
                  variant={hatRaumLink ? 'outlined' : 'contained'}
                  color={hatRaumLink ? 'primary' : 'error'}
                  startIcon={<LinkIcon />}
                  onClick={() =>
                    einfuegen(`<p><a href="${RAUM_LINK}"><b>&rarr; Jetzt zum Webinar-Raum</b></a></p>`, 'html_body')
                  }
                >
                  Raum-Link einfügen
                </Button>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                Zeitangaben, die immer stimmen — fügt ein in: <strong>{zielName}</strong> (zuletzt angeklicktes Feld)
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {[...ZEIT_PLATZHALTER, ...SONST_PLATZHALTER].map((p) => (
                <Tooltip key={p.token} title={`Der Empfänger liest: ${p.liest}`}>
                  <Chip size="small" variant="outlined" label={p.knopf} onClick={() => einfuegen(p.token)} />
                </Tooltip>
              ))}
            </Stack>
          </Box>
        )}
        <TinyMCEEditor
          ref={editorRef}
          value={slot.html_body || ''}
          onChange={(html) => onPatch('html_body', html)}
          onFocus={() => setZiel('html_body')}
          height={320}
          disabled={gesperrt}
        />
      </Feld>

      {slot.trigger_type === 'offset' && !gesperrt && (
        <MehrOptionen
          titel="Versandzeit ändern"
          erklaerung="Optional — die Versandzeit ist vorbelegt. Nur ändern, wenn die Mail zu einer anderen Zeit rausgehen soll."
        >
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              label="Art"
              size="small"
              sx={{ minWidth: 190 }}
              value={slot.send_clock ? 'uhrzeit' : 'minuten'}
              onChange={(e) => onZeitModus(e.target.value as 'minuten' | 'uhrzeit')}
            >
              <MenuItem value="minuten">Minuten vor Beginn</MenuItem>
              <MenuItem value="uhrzeit">Tag + Uhrzeit</MenuItem>
            </TextField>
            {!slot.send_clock && (
              <TextField
                label="Minuten (negativ = vor Beginn)"
                type="number"
                size="small"
                sx={{ minWidth: 240 }}
                value={slot.offset_minutes ?? 0}
                onChange={(e) => onPatch('offset_minutes', Number(e.target.value))}
              />
            )}
            {slot.send_clock && (
              <>
                <TextField
                  select
                  label="Tag"
                  size="small"
                  sx={{ minWidth: 170 }}
                  value={Number(slot.offset_minutes ?? 0)}
                  onChange={(e) => onPatch('offset_minutes', Number(e.target.value))}
                >
                  {UHRZEIT_TAGE.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Uhrzeit"
                  type="time"
                  size="small"
                  sx={{ minWidth: 130 }}
                  value={slot.send_clock}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  onChange={(e) => onPatch('send_clock', e.target.value || null)}
                />
              </>
            )}
          </Stack>
        </MehrOptionen>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button variant="contained" size="small" onClick={onSave} disabled={gesperrt || !dirty}>
          Mail {n} speichern
        </Button>
        <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} onClick={onPreview}>
          Vorschau
        </Button>
        <Tooltip title="Schickt den gespeicherten Stand dieser Mail als Test. Betreff beginnt mit „[TEST]“.">
          <Button variant="outlined" size="small" startIcon={<SendIcon />} onClick={onTest}>
            {testAn.eigene ? 'Testmail an mich' : 'Testmail an den Admin'}
          </Button>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {testAn.eigene
            ? `geht an ${testAn.adresse}`
            : `geht an ${testAn.adresse} — deine Adresse endet nicht auf @yes-investmedia.de, @finanztrends.de oder @finanztrends.info`}
        </Typography>
      </Stack>
    </Paper>
  );
};

/**
 * „Text aus früherem Webinar …“ an EINER Mail (Persona-Runde 3: Mail 1+2 fertig, 3+4 leer — das Übernehmen aller
 * vier Mails passte nicht). Liste aus `ladeBeispiele('reminder.<N>.html_body')`, Vorschau nur als Text. Übernehmen
 * setzt das HTML in DIESEN Editor und speichert nicht; steht schon Text drin, wird vorher gefragt.
 */
const TextVorlage: React.FC<{
  n: number;
  webinarId: string;
  hatText: boolean;
  onUebernehmen: (html: string) => void;
}> = ({ n, webinarId, hatText, onUebernehmen }) => {
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const [liste, setListe] = useState<Beispiel[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [rueckfrage, setRueckfrage] = useState<Beispiel | null>(null);

  const oeffnen = async (el: HTMLElement) => {
    setAnker(el);
    if (liste) return;
    setLaedt(true);
    setFehler(null);
    const r = await ladeBeispiele(`reminder.${n}.html_body`, webinarId);
    setLaedt(false);
    if (r.success) setListe(r.data ?? []);
    else setFehler(r.error ?? 'Texte konnten nicht geladen werden');
  };
  const nehmen = (b: Beispiel) => {
    if (hatText) {
      setRueckfrage(b);
      return;
    }
    onUebernehmen(b.wert);
    setAnker(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => void oeffnen(e.currentTarget)}
        sx={{ textTransform: 'none', fontSize: 12, py: 0, minHeight: 0 }}
      >
        Text aus früherem Webinar …
      </Button>
      <Popover
        open={!!anker}
        anchorEl={anker}
        onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 520, maxWidth: '94vw', p: 1.5 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Text von Mail {n} aus früheren Webinaren
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Übernehmen ersetzt nur den Text DIESER Mail; gespeichert wird erst mit „Mail {n} speichern“. Danach Titel, Datum
          und Namen anpassen — die Prüfung meldet fremde Webinarnamen und falsche Zeitangaben nach dem Speichern.
        </Typography>
        {laedt && <CircularProgress size={18} />}
        {fehler && (
          <Typography variant="body2" color="error">
            {fehler}
          </Typography>
        )}
        {liste && liste.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine früheren Texte für diese Mail gefunden.
          </Typography>
        )}
        {liste?.map((b, i) => (
          <Box key={`${b.webinarId}-${i}`} sx={{ borderTop: i ? '1px solid #eee' : 'none', py: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {b.titel}
              </Typography>
              {b.datum && (
                <Typography variant="caption" color="text.secondary">
                  {datumDe(b.datum)}
                </Typography>
              )}
              {b.gleichesProdukt && <Chip size="small" label="gleiches Produkt" />}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" onClick={() => nehmen(b)}>
                Übernehmen
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {htmlAlsText(b.wert)}
            </Typography>
          </Box>
        ))}
      </Popover>
      <Dialog open={Boolean(rueckfrage)} onClose={() => setRueckfrage(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Text von Mail {n} ersetzen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Ersetzt den Text dieser Mail — nur diese Mail. Gespeichert wird erst mit „Mail {n} speichern“; bis dahin
            lässt sich das durch Neuladen der Seite verwerfen.
          </Typography>
          {rueckfrage && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Vorlage: {rueckfrage.titel}
              {rueckfrage.datum ? ` (${datumDe(rueckfrage.datum)})` : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRueckfrage(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (rueckfrage) onUebernehmen(rueckfrage.wert);
              setRueckfrage(null);
              setAnker(null);
            }}
          >
            Text ersetzen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReminderTab;
